import React, { useEffect, useMemo } from 'react';
import { Menu } from '@headlessui/react';
import { ChevronDownIcon as ArrowIcon } from '@heroicons/react/24/solid';
import { useFormat } from 'helpers/hooks/useFormat';
import { useProductList } from '../../context';

interface SortFacetProps {
  isMobile?: boolean;
  className?: string;
}

const SortFacet: React.FC<SortFacetProps> = ({ isMobile = false, className }) => {
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });

  const { replaceSort, activeSort, sortOptions } = useProductList();

  const selectedSortLabel = useMemo(() => {
    return (
      sortOptions?.find((option) => option.attribute === activeSort?.attribute && option.value === activeSort.value)
        ?.label ?? formatProductMessage({ id: 'sortBy', defaultMessage: 'Sort by' })
    );
  }, [activeSort, sortOptions, formatProductMessage]);

  return (
    <div className={`flex items-center gap-10 ${className} `}>
      <div className="hidden text-sm text-gray-600 lg:block">Sort by:</div>
      <Menu as="div" className="relative w-full lg:w-auto">
        {({ open }) => (
          <>
            <Menu.Button className={'min-w-full lg:min-w-260'}>
              <div className="group flex items-center justify-between gap-10 rounded-[5px] border-2 border-gray-600 px-12 py-6">
                <span className="text-xs">{selectedSortLabel}</span>
                <ArrowIcon className="mt-2 w-16 stroke-secondary-black" />
              </div>
            </Menu.Button>
            <Menu.Items
              static
              className={`absolute right-0 w-full origin-top-right translate-y-10 rounded-[5px] border-2 border-gray-600 bg-white ${
                open ? 'z-20 scale-100' : 'z-[-1] scale-95 opacity-0'
              }`}
            >
              {sortOptions?.map(({ label, attribute, value, id }) => (
                <Menu.Item as="div" key={`${id}-${isMobile ? 'mobile' : 'desktop'}`}>
                  {({ active }) => (
                    <div
                      className={`m-2 cursor-pointer rounded border-2 border-transparent px-4 py-2 transition hover:border-blue-700 hover:bg-blue-100 hover:text-blue-700 ${
                        attribute === activeSort?.attribute && value === activeSort.value
                          ? 'bg-neutral-400'
                          : 'bg-transparent'
                      }`}
                      onClick={() => replaceSort({ attribute, label, value })}
                    >
                      <span className="text-xs">{label}</span>
                    </div>
                  )}
                </Menu.Item>
              ))}
            </Menu.Items>
          </>
        )}
      </Menu>
    </div>
  );
};

export default SortFacet;
